/**
 * employerMatchesHelpers.js — RC 1.30.F pure helpers.
 *
 * Extracted from `EmployerRoleMatchesPage` so they can be unit-
 * tested without pulling React / RTL into the test bundle.
 *
 * Rules encoded here:
 *   • Anonymous labelling — a match is identified by its SORT-ORDER
 *     index only. NEVER by candidate_id.
 *   • Percentage formatting for fit_score (stored as 0.0–1.0).
 *   • Local filter-by-signal + sort-by-{fit_desc,fit_asc,recent}.
 *   • PII guard — `assertNoPII(match)` returns the list of offending
 *     keys, empty when clean. Used defensively in tests to prove
 *     the rendered payload carries no candidate identity.
 */

export function anonLabel(index) {
  return `Match ${String(index + 1).padStart(3, '0')}`;
}

export function formatFitPercent(score) {
  if (typeof score !== 'number' || Number.isNaN(score)) return '—';
  return `${Math.round(score * 100)}%`;
}

/**
 * Relative-time formatter — RC 1.30.F timestamp polish.
 *   • <60s  → 'just now'
 *   • <60m  → 'N minutes ago'
 *   • <24h  → 'N hours ago'
 *   • ≥24h  → absolute "Mon D, hh:mm AM"
 *
 * Falls back to '—' on null / invalid input. `now` is a seam for
 * deterministic tests; defaults to `Date.now()`.
 */
export function formatRelativeTime(iso, now = Date.now()) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffSec = Math.floor((now - d.getTime()) / 1000);
  if (diffSec < 0)   return formatAbsolute(d);
  if (diffSec < 60)  return 'just now';
  const m = Math.floor(diffSec / 60);
  if (m < 60)        return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24)        return `${h} hour${h === 1 ? '' : 's'} ago`;
  return formatAbsolute(d);
}

function formatAbsolute(d) {
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export const ALLOWED_MATCH_KEYS = Object.freeze([
  'id',
  'fit_score',
  'confidence',
  'decision_signal',
  'match_rationale_json',
  'surfaced_at',
]);

const FORBIDDEN_IDENTITY_KEYS = [
  'candidate_id',
  'user_id',
  'email',
  'full_name',
  'name',
  'phone',
  'linkedin',
  'profile_url',
];

/**
 * Scan a match object (and its rationale) for any PII-ish keys.
 * Returns a list of offending paths. Empty list = clean.
 */
export function assertNoPII(match) {
  const offenders = [];
  if (match && typeof match === 'object') {
    for (const k of Object.keys(match)) {
      if (FORBIDDEN_IDENTITY_KEYS.includes(k)) offenders.push(k);
    }
    const r = match.match_rationale_json;
    if (r && typeof r === 'object') {
      for (const k of Object.keys(r)) {
        if (FORBIDDEN_IDENTITY_KEYS.includes(k)) {
          offenders.push(`match_rationale_json.${k}`);
        }
      }
    }
  }
  return offenders;
}

/**
 * Local sort + filter. Pure; no side effects.
 * filter: 'all' | 'surface' | 'hold' | 'pass'
 * sort:   'fit_desc' | 'fit_asc' | 'recent'
 */
export function sortAndFilterMatches(matches, filter, sort) {
  let rows = Array.isArray(matches) ? matches : [];
  if (filter && filter !== 'all') {
    rows = rows.filter((m) => m.decision_signal === filter);
  }
  if (sort === 'fit_asc') {
    rows = [...rows].sort((a, b) => (a.fit_score ?? 0) - (b.fit_score ?? 0));
  } else if (sort === 'recent') {
    rows = [...rows].sort(
      (a, b) => new Date(b.surfaced_at) - new Date(a.surfaced_at),
    );
  } else {
    rows = [...rows].sort((a, b) => (b.fit_score ?? 0) - (a.fit_score ?? 0));
  }
  return rows;
}
